/**
 * Deliberation API Service
 * 
 * Serviço para consumir APIs de deliberação.
 */
import { supabase } from '../lib/supabase';

const API_BASE_URL = import.meta.env.VITE_RAG_API_URL || 'http://localhost:8000';

export interface DeliberationRun {
    id: string;
    status: 'running' | 'completed' | 'error' | 'timeout';
    question: string;
    context?: string;
    created_at: string;
    completed_at?: string;
    final_decision?: string;
    confidence?: number;
    action_plan?: string[];
}

export interface DeliberationEvent {
    id: string;
    run_id: string;
    seq: number;
    type: 'stage_started' | 'stage_completed' | 'error' | 'done' | 'degraded';
    stage?: string;
    timestamp: string;
    payload: Record<string, any>;
}

export interface DeliberationDetail {
    run: DeliberationRun;
    events: DeliberationEvent[];
    total_events: number;
}

/**
 * Lista deliberações do usuário
 */
export async function listDeliberations(
    limit: number = 50,
    offset: number = 0
): Promise<DeliberationRun[]> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
        throw new Error('Não autenticado');
    }

    const response = await fetch(
        `${API_BASE_URL}/deliberate/runs?limit=${limit}&offset=${offset}`,
        {
            headers: {
                'Authorization': `Bearer ${session.access_token}`,
            },
        }
    );

    if (!response.ok) {
        throw new Error(`Erro ao listar deliberações: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Busca detalhes de uma deliberação
 */
export async function getDeliberationDetail(runId: string): Promise<DeliberationDetail> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
        throw new Error('Não autenticado');
    }

    const response = await fetch(
        `${API_BASE_URL}/deliberate/runs/${runId}`,
        {
            headers: {
                'Authorization': `Bearer ${session.access_token}`,
            },
        }
    );

    if (response.status === 404) {
        throw new Error('Deliberação não encontrada');
    }

    if (!response.ok) {
        throw new Error(`Erro ao buscar deliberação: ${response.statusText}`);
    }

    return response.json();
}

/**
 * Inicia nova deliberação (streaming SSE)
 */
export async function startDeliberation(
    question: string,
    context: string = ''
): Promise<EventSource> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
        throw new Error('Não autenticado');
    }

    // SSE não suporta POST direto, então usamos fetch primeiro
    const response = await fetch(`${API_BASE_URL}/deliberate/stream`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question, context }),
    });

    if (response.status === 429) {
        throw new Error('Deliberação já em andamento');
    }

    if (!response.ok) {
        throw new Error(`Erro ao iniciar deliberação: ${response.statusText}`);
    }

    // Criar EventSource para SSE
    // Nota: EventSource não suporta headers customizados nativamente
    // Alternativa: usar fetch com ReadableStream
    return createSSEConnection(question, context, session.access_token);
}

/**
 * Cria conexão SSE usando fetch + ReadableStream
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function createSSEConnection(
    _graphId: string,
    _context: string,
    _token: string
): EventSource {
    // Por limitação do EventSource, vamos simular com fetch
    // Em produção, considerar usar library como eventsource-parser
    throw new Error('SSE streaming será implementado com fetch + ReadableStream');
}

/**
 * Cancela uma deliberação em andamento
 */
export async function cancelDeliberation(runId: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
        throw new Error('Não autenticado');
    }

    const response = await fetch(`${API_BASE_URL}/deliberate/runs/${runId}/cancel`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Falha ao cancelar' }));
        throw new Error(error.detail || 'Falha ao cancelar deliberação');
    }
}

/**
 * Arquiva (soft delete) uma deliberação concluída ou com erro
 */
export async function archiveDeliberation(runId: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
        throw new Error('Não autenticado');
    }

    const response = await fetch(`${API_BASE_URL}/deliberate/runs/${runId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Falha ao arquivar' }));
        throw new Error(error.detail || 'Falha ao arquivar deliberação');
    }
}
